import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Heading, Text, HStack, VStack, Button, Table, Thead, Tbody, Tr, Th, Td,
  IconButton, Badge, Spinner, Alert, AlertIcon, useToast, Code, Icon,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
  FormControl, FormLabel, FormHelperText, Input, Select, useDisclosure, Switch,
} from '@chakra-ui/react';
import { FiTrash2, FiPlus, FiKey, FiCopy } from 'react-icons/fi';

const API_BASE = '/api/v1';

const getAuthHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Token ${localStorage.getItem('assistant_token')}`,
});

function McpKeys() {
  const toast = useToast();
  const [keys, setKeys] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [tenantId, setTenantId] = useState('');
  const [name, setName] = useState('');
  const [creating, setCreating] = useState(false);
  const [revealedKey, setRevealedKey] = useState(null);

  const { isOpen: isCreateOpen, onOpen: onCreateOpen, onClose: onCreateClose } = useDisclosure();
  const { isOpen: isRevealOpen, onOpen: onRevealOpen, onClose: onRevealClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const [keyToDelete, setKeyToDelete] = useState(null);

  const fetchAll = useCallback(() => {
    setLoading(true);
    Promise.all([
      fetch(`${API_BASE}/mcp-keys/`, { headers: getAuthHeaders() }).then(res => res.json()),
      fetch(`${API_BASE}/v1/tenants`, { headers: getAuthHeaders() }).then(res => res.json()),
    ])
      .then(([keysData, tenantsData]) => {
        setKeys(Array.isArray(keysData) ? keysData : []);
        setTenants(tenantsData.tenants || []);
        setError(null);
      })
      .catch(() => setError('Could not load MCP API keys.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openCreate = () => {
    setTenantId('');
    setName('');
    onCreateOpen();
  };

  const handleCreate = async () => {
    if (!tenantId) {
      toast({ title: 'Select an instance first', status: 'warning', duration: 3000 });
      return;
    }
    setCreating(true);
    try {
      const res = await fetch(`${API_BASE}/mcp-keys/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ tenant_id: tenantId, name }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast({ title: data.error || 'Failed to create key', status: 'error', duration: 4000 });
        return;
      }
      setKeys(prev => [data, ...prev]);
      onCreateClose();
      setRevealedKey(data);
      onRevealOpen();
    } catch (e) {
      toast({ title: 'Error creating key', status: 'error', duration: 4000 });
    } finally {
      setCreating(false);
    }
  };

  const toggleActive = async (key) => {
    try {
      const res = await fetch(`${API_BASE}/mcp-keys/${key.id}/`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ is_active: !key.is_active }),
      });
      const data = await res.json();
      if (res.ok) {
        setKeys(prev => prev.map(k => (k.id === key.id ? data : k)));
      } else {
        toast({ title: data.error || 'Failed to update key', status: 'error', duration: 4000 });
      }
    } catch (e) {
      toast({ title: 'Error updating key', status: 'error', duration: 4000 });
    }
  };

  const confirmDelete = (key) => {
    setKeyToDelete(key);
    onDeleteOpen();
  };

  const handleDelete = async () => {
    if (!keyToDelete) return;
    try {
      const res = await fetch(`${API_BASE}/mcp-keys/${keyToDelete.id}/`, { method: 'DELETE', headers: getAuthHeaders() });
      if (res.ok) {
        setKeys(prev => prev.filter(k => k.id !== keyToDelete.id));
        toast({ title: 'Key revoked', status: 'success', duration: 3000 });
      } else {
        toast({ title: 'Error revoking key', status: 'error', duration: 4000 });
      }
    } catch (e) {
      toast({ title: 'Error revoking key', status: 'error', duration: 4000 });
    }
    onDeleteClose();
    setKeyToDelete(null);
  };

  const copyKey = (value) => {
    navigator.clipboard?.writeText(value);
    toast({ title: 'Copied to clipboard', status: 'success', duration: 2000 });
  };

  if (loading) return <Box textAlign="center" py={20}><Spinner size="xl" color="purple.500" /></Box>;

  return (
    <Box>
      <HStack justify="space-between" mb={6}>
        <VStack align="start" spacing={1}>
          <Heading size="lg">MCP API Keys</Heading>
          <Text color="gray.500">
            Tenant-scoped bearer tokens for external MCP clients (e.g. Claude Desktop). Each key is
            permanently pinned to one instance — a leaked key can never be pointed at another tenant.
          </Text>
        </VStack>
        <Button leftIcon={<FiPlus />} colorScheme="purple" onClick={openCreate}>Create Key</Button>
      </HStack>

      {error && <Alert status="error" mb={4} borderRadius="md"><AlertIcon />{error}</Alert>}

      <Box bg="white" borderRadius="lg" boxShadow="md" overflowX="auto">
        <Table variant="simple">
          <Thead>
            <Tr>
              <Th>Name</Th>
              <Th>Instance</Th>
              <Th>Key</Th>
              <Th>Status</Th>
              <Th>Last Used</Th>
              <Th>Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {keys.map((key) => (
              <Tr key={key.id}>
                <Td fontWeight="medium">{key.name || <Text as="span" color="gray.400">Unnamed</Text>}</Td>
                <Td>{key.tenant_name}</Td>
                <Td><Code fontSize="xs">{key.key_masked}</Code></Td>
                <Td>
                  <HStack>
                    <Switch size="sm" isChecked={key.is_active} onChange={() => toggleActive(key)} colorScheme="purple" />
                    <Badge colorScheme={key.is_active ? 'green' : 'red'}>{key.is_active ? 'Active' : 'Revoked'}</Badge>
                  </HStack>
                </Td>
                <Td fontSize="xs">{key.last_used_at ? new Date(key.last_used_at).toLocaleString() : 'Never'}</Td>
                <Td>
                  <IconButton
                    icon={<FiTrash2 />}
                    size="sm"
                    colorScheme="red"
                    variant="ghost"
                    onClick={() => confirmDelete(key)}
                    aria-label="Delete key"
                  />
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
        {keys.length === 0 && (
          <Box textAlign="center" py={10}>
            <Icon as={FiKey} boxSize={7} color="gray.400" />
            <Text color="gray.500" mt={2}>No MCP API keys yet</Text>
          </Box>
        )}
      </Box>

      {/* Create Modal */}
      <Modal isOpen={isCreateOpen} onClose={onCreateClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Create MCP API Key</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Instance</FormLabel>
                <Select placeholder="Select an instance..." value={tenantId} onChange={(e) => setTenantId(e.target.value)}>
                  {tenants.map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </Select>
                <FormHelperText>This key will only ever be able to read this one instance's data.</FormHelperText>
              </FormControl>
              <FormControl>
                <FormLabel>Name</FormLabel>
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Claude Desktop" />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onCreateClose}>Cancel</Button>
            <Button colorScheme="purple" onClick={handleCreate} isLoading={creating}>Create</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Reveal-once Modal */}
      <Modal isOpen={isRevealOpen} onClose={onRevealClose} closeOnOverlayClick={false}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Key Created</ModalHeader>
          <ModalBody>
            <Alert status="warning" borderRadius="md" mb={4}>
              <AlertIcon />
              Copy this now — it won't be shown again. If you lose it, revoke it and create a new one.
            </Alert>
            <HStack bg="gray.50" p={3} borderRadius="md">
              <Code fontSize="sm" wordBreak="break-all" flex="1" bg="transparent">{revealedKey?.key}</Code>
              <IconButton
                icon={<FiCopy />}
                size="sm"
                aria-label="Copy key"
                onClick={() => copyKey(revealedKey?.key)}
              />
            </HStack>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="purple" onClick={onRevealClose}>Done</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isDeleteOpen} onClose={onDeleteClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Revoke Key</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            Are you sure you want to revoke <strong>{keyToDelete?.name || 'this key'}</strong>? Any client using it
            will immediately lose access. This cannot be undone.
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onDeleteClose}>Cancel</Button>
            <Button colorScheme="red" onClick={handleDelete}>Revoke</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

export default McpKeys;
